import * as React from 'react';

export interface BreadcrumbLink {
  name: string;
  url: string;
}

export interface BreadcrumbsState {
  active: string;
  links: BreadcrumbLink[];
}

interface BreadcrumbsContextValues {
  breadcrumbs: BreadcrumbsState | null;
  setBreadcrumbs: React.Dispatch<React.SetStateAction<BreadcrumbsState | null>>;
}

const BreadcrumbsContext = React.createContext<BreadcrumbsContextValues>({
  breadcrumbs: null,
  setBreadcrumbs: () => {},
});

export const BreadcrumbsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = React.useState<BreadcrumbsState | null>(null);
  const value = React.useMemo(
    () => ({ breadcrumbs, setBreadcrumbs }),
    [breadcrumbs, setBreadcrumbs],
  );
  return <BreadcrumbsContext.Provider value={value}>{children}</BreadcrumbsContext.Provider>;
};

function isValidLink(l: unknown): l is BreadcrumbLink {
  return l != null && typeof (l as any).name === 'string' && typeof (l as any).url === 'string';
}

export const useBreadcrumbs = (state?: BreadcrumbsState) => {
  const { setBreadcrumbs } = React.useContext(BreadcrumbsContext);

  const stateRef = React.useRef<BreadcrumbsState | undefined>(state);
  stateRef.current = state;

  const validLinks = (state?.links ?? []).filter(isValidLink);

  const sig = React.useMemo(() => {
    if (!state) return '';
    return `${state.active}::${validLinks.map((l) => `${l.name}|${l.url}`).join('>')}`;
  }, [state?.active, validLinks.length, ...validLinks.flatMap((l) => [l.name, l.url])]);

  const lastSigRef = React.useRef<string>('');

  React.useEffect(() => {
    if (!sig) {
      lastSigRef.current = '';
      setBreadcrumbs(null);
      return;
    }

    if (sig !== lastSigRef.current) {
      lastSigRef.current = sig;
      setBreadcrumbs(stateRef.current ?? null);
    }

    return () => {
      const sigAtMount = sig;
      lastSigRef.current = '';
      setBreadcrumbs((prev) => {
        if (!prev) return prev;
        const prevLinks = (prev.links ?? []).filter(isValidLink);
        const prevSig = `${prev.active}::${prevLinks.map((l) => `${l.name}|${l.url}`).join('>')}`;
        return prevSig === sigAtMount ? null : prev;
      });
    };
  }, [sig, setBreadcrumbs]);
};

export const useBreadcrumbsContext = () => {
  return React.useContext(BreadcrumbsContext);
};
